import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaDrawPolygon,
  FaInfoCircle,
  FaMapMarkedAlt,
  FaRulerCombined,
  FaTimes,
} from "react-icons/fa";

import {
  computePointsAreaHa,
  formatAreaHa,
  formatDistanceM,
} from "../../utils/mapMeasurements";
import {
  measureTapeAcrossTracks,
  projectOntoBearing,
  snapToNearestTrack,
} from "../../utils/mapTrackMeasureUtils";

const CLICK_DELAY_MS = 250;

function clearOverlays(overlays) {
  overlays.forEach((overlay) => {
    if (overlay && overlay.setMap) overlay.setMap(null);
  });
  overlays.length = 0;
}

const TAPE_COLOR = "#FF6F00";

function formatGaps(gaps) {
  if (!gaps || !gaps.length) return "—";
  return gaps.map((gap) => formatDistanceM(gap)).join(" · ");
}

function drawTape(map, maps, tape) {
  if (!tape || !tape.origin) return [];
  const overlays = [];
  const ends = [tape.origin];
  if (tape.end) ends.push(tape.end);

  if (ends.length >= 2) {
    overlays.push(
      new maps.Polyline({
        map,
        path: ends,
        strokeColor: TAPE_COLOR,
        strokeWeight: 3,
        geodesic: true,
        clickable: false,
        zIndex: 1600,
      })
    );
  }

  ends.forEach((point, index) => {
    overlays.push(
      new maps.Marker({
        position: point,
        map,
        clickable: false,
        zIndex: 2000,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: TAPE_COLOR,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 1,
        },
        label: {
          text: String(index + 1),
          color: "#ffffff",
          fontSize: "11px",
          fontWeight: "700",
        },
      })
    );
  });

  const crossings = (tape.result && tape.result.crossings) || [];
  crossings.forEach((crossing) => {
    overlays.push(
      new maps.Marker({
        position: { lat: crossing.lat, lng: crossing.lng },
        map,
        clickable: false,
        zIndex: 2100,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: "#111111",
          fillOpacity: 0.9,
          strokeColor: "#FFD54F",
          strokeWeight: 2,
        },
      })
    );
  });

  return overlays;
}

function drawMeasure(map, maps, tool, points, cursor, finished) {
  const overlays = [];
  const livePath =
    !finished && cursor && points.length ? points.concat(cursor) : points.slice();

  points.forEach((point, index) => {
    overlays.push(
      new maps.Marker({
        position: point,
        map,
        clickable: false,
        zIndex: 2000,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: "#f5c518",
          fillOpacity: 1,
          strokeColor: "#1b1b1b",
          strokeWeight: 1,
        },
        label: {
          text: String(index + 1),
          color: "#1b1b1b",
          fontSize: "11px",
          fontWeight: "700",
        },
      })
    );
  });

  if (tool === "area" && livePath.length >= 2) {
    const closeShape = finished || points.length >= 3;
    if (closeShape) {
      overlays.push(
        new maps.Polygon({
          map,
          paths: finished ? points : livePath,
          strokeColor: "#f5c518",
          strokeWeight: 3,
          fillColor: "#f5c518",
          fillOpacity: 0.22,
          geodesic: true,
          clickable: false,
          zIndex: 1500,
        })
      );
    } else {
      overlays.push(
        new maps.Polyline({
          map,
          path: livePath,
          strokeColor: "#f5c518",
          strokeWeight: 3,
          geodesic: true,
          clickable: false,
          zIndex: 1500,
        })
      );
    }
  }

  return overlays;
}

function latLngFromMouse(map, maps, event) {
  const projection = map.getProjection && map.getProjection();
  const bounds = map.getBounds && map.getBounds();
  if (!projection || !bounds || !projection.fromLatLngToPoint) return null;
  const rect = map.getDiv().getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
  const scale = Math.pow(2, map.getZoom());
  const latLng = projection.fromPointToLatLng(
    new maps.Point(x / scale + bottomLeft.x, y / scale + topRight.y)
  );
  if (!latLng) return null;
  return { lat: latLng.lat(), lng: latLng.lng() };
}

export default function DashboardQuickMeasure({
  t,
  map,
  maps,
  farmId,
  lines,
  landplotPickMode = false,
  onToggleLandplotPickMode,
}) {
  const [tool, setTool] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [points, setPoints] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [finished, setFinished] = useState(false);
  const [tape, setTape] = useState(null);
  const overlaysRef = useRef([]);
  const rootRef = useRef(null);
  const linesRef = useRef(lines || []);
  const tapeRef = useRef(null);
  linesRef.current = lines || [];
  tapeRef.current = tape;

  useEffect(() => {
    clearOverlays(overlaysRef.current);
    if (!map || !maps || !tool) return undefined;
    overlaysRef.current =
      tool === "distance"
        ? drawTape(map, maps, tape)
        : drawMeasure(map, maps, tool, points, cursor, finished);
    return undefined;
  }, [map, maps, tool, points, cursor, finished, tape]);

  useEffect(() => {
    return () => clearOverlays(overlaysRef.current);
  }, []);

  useEffect(() => {
    if (!map || !maps || !tool || finished) return undefined;

    let clickTimer = null;
    let moveFrame = 0;
    const previousZoom = map.get("disableDoubleClickZoom");

    map.setOptions({
      disableDoubleClickZoom: true,
      draggableCursor: "crosshair",
    });

    const mapDiv = map.getDiv();
    const host = mapDiv.querySelector(".gm-style") || mapDiv;
    const catcher = document.createElement("div");
    catcher.setAttribute("data-quick-measure", "true");
    catcher.style.position = "absolute";
    catcher.style.left = "0";
    catcher.style.top = "0";
    catcher.style.right = "0";
    catcher.style.bottom = "0";
    catcher.style.zIndex = "1000";
    catcher.style.cursor = "crosshair";
    catcher.style.background = "transparent";
    host.appendChild(catcher);

    const readPoint = (event) => latLngFromMouse(map, maps, event);
    let drag = null;

    const publishTape = (next) => {
      tapeRef.current = next;
      cancelAnimationFrame(moveFrame);
      moveFrame = requestAnimationFrame(() => setTape(next));
    };

    const tapeFromPoints = (current, end, done) => {
      const result = measureTapeAcrossTracks(current.origin, end, linesRef.current, {
        snapped: current.snapped,
        tapeBearing: current.bearing,
      });
      return { ...current, end, result, finished: Boolean(done) };
    };

    const previewTape = (point) => {
      const current = tapeRef.current;
      if (!current || !current.origin || current.finished || !point) return;
      const end = current.snapped
        ? projectOntoBearing(current.origin, current.bearing, point)
        : point;
      publishTape(tapeFromPoints(current, end, false));
    };

    const addPoint = (event) => {
      const point = readPoint(event);
      if (!point) return;
      event.preventDefault();
      event.stopPropagation();
      clearTimeout(clickTimer);
      clickTimer = setTimeout(() => {
        setPoints((current) => current.concat(point));
      }, CLICK_DELAY_MS);
    };

    const beginTape = (point) => {
      const snap = snapToNearestTrack(point, linesRef.current);
      publishTape(
        snap
          ? {
              origin: snap.point,
              bearing: snap.tapeBearing,
              snapped: true,
              end: null,
              finished: false,
              result: null,
            }
          : {
              origin: point,
              bearing: null,
              snapped: false,
              end: null,
              finished: false,
              result: null,
            }
      );
    };

    const handleDown = (event) => {
      if (event.button !== 0 || event.target !== catcher) return;
      if (tool === "distance") {
        const current = tapeRef.current;
        if (!current || !current.origin || current.finished) {
          const point = readPoint(event);
          if (!point) return;
          beginTape(point);
          drag = {
            x: event.clientX,
            y: event.clientY,
            moved: false,
            startedOrigin: true,
          };
        } else {
          drag = {
            x: event.clientX,
            y: event.clientY,
            moved: false,
            startedOrigin: false,
          };
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      drag = { x: event.clientX, y: event.clientY, moved: false };
      event.preventDefault();
      event.stopPropagation();
    };

    const handleMove = (event) => {
      if (tool === "distance") {
        const current = tapeRef.current;
        if (current && current.origin && !current.finished) {
          if (drag) {
            const dx = event.clientX - drag.x;
            const dy = event.clientY - drag.y;
            if (Math.hypot(dx, dy) > 6) drag.moved = true;
          }
          const pulling = Boolean(drag && drag.moved);
          if (!pulling && event.target !== catcher) return;
          const point = readPoint(event);
          if (point) previewTape(point);
          return;
        }
      }
      if (drag) {
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved && Math.hypot(dx, dy) <= 6) return;
        drag.moved = true;
        drag.x = event.clientX;
        drag.y = event.clientY;
        if (map.panBy) map.panBy(-dx, -dy);
        return;
      }
      if (event.target !== catcher) return;
      const point = readPoint(event);
      if (!point) return;
      cancelAnimationFrame(moveFrame);
      moveFrame = requestAnimationFrame(() => setCursor(point));
    };

    const handleUp = (event) => {
      if (!drag || event.button !== 0) return;
      const wasDrag = drag.moved;
      const startedOrigin = drag.startedOrigin;
      drag = null;
      event.preventDefault();
      event.stopPropagation();
      if (tool === "distance") {
        const current = tapeRef.current;
        if (!current || !current.origin || current.finished) return;
        if (startedOrigin && !wasDrag) return;
        const point = readPoint(event);
        const end = point
          ? current.snapped
            ? projectOntoBearing(current.origin, current.bearing, point)
            : point
          : current.end;
        if (!end) return;
        cancelAnimationFrame(moveFrame);
        const next = tapeFromPoints(current, end, true);
        tapeRef.current = next;
        setTape(next);
        return;
      }
      if (wasDrag) return;
      addPoint(event);
    };

    const handleDoubleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      clearTimeout(clickTimer);
      if (tool === "area") setFinished(true);
    };

    catcher.addEventListener("dblclick", handleDoubleClick);
    window.addEventListener("mousedown", handleDown, true);
    window.addEventListener("mousemove", handleMove, true);
    window.addEventListener("mouseup", handleUp, true);

    return () => {
      clearTimeout(clickTimer);
      cancelAnimationFrame(moveFrame);
      catcher.removeEventListener("dblclick", handleDoubleClick);
      window.removeEventListener("mousedown", handleDown, true);
      window.removeEventListener("mousemove", handleMove, true);
      window.removeEventListener("mouseup", handleUp, true);
      if (catcher.parentNode) catcher.parentNode.removeChild(catcher);
      map.setOptions({
        disableDoubleClickZoom: Boolean(previousZoom),
        draggableCursor: null,
      });
    };
  }, [map, maps, tool, finished]);

  useEffect(() => {
    if (!helpOpen && !tool) return undefined;
    const handleDocumentClick = (event) => {
      const root = rootRef.current;
      if (root && event.target && root.contains(event.target)) return;
      const target = event.target;
      const onMap =
        target &&
        target.closest &&
        (target.closest("[data-quick-measure]") ||
          target.closest(".gm-style") ||
          target.closest(".dashboard-map-wrapper"));
      if (tool && onMap) return;
      if (helpOpen) setHelpOpen(false);
      if (tool) {
        setTool(null);
        setPoints([]);
        setCursor(null);
        setFinished(false);
        tapeRef.current = null;
        setTape(null);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [helpOpen, tool]);

  function resetDraft() {
    setPoints([]);
    setCursor(null);
    setFinished(false);
    tapeRef.current = null;
    setTape(null);
  }

  function chooseTool(nextTool) {
    setHelpOpen(false);
    if (tool === nextTool) {
      setTool(null);
      resetDraft();
      return;
    }
    setTool(nextTool);
    resetDraft();
  }

  const tapeResult = tape && tape.result;
  const areaHa = computePointsAreaHa(points);
  const ready = Boolean(map && maps);
  const hasTracks = Array.isArray(lines) && lines.length > 0;

  return (
    <div className="dashboard-quick-measure" ref={rootRef}>
      <button
        type="button"
        className={`dashboard-quick-measure-btn${
          tool === "distance" ? " is-active" : ""
        }`}
        aria-pressed={tool === "distance"}
        title={t(
          "Cruza vários rastros na transversal e calcula a média da passada"
        )}
        disabled={!ready}
        onClick={() => chooseTool("distance")}
      >
        <FaRulerCombined aria-hidden />
        <span>{t("Régua")}</span>
      </button>
      <button
        type="button"
        className={`dashboard-quick-measure-btn${
          landplotPickMode ? " is-active" : ""
        }`}
        aria-pressed={Boolean(landplotPickMode)}
        title={t(
          "Escolher talhão: ligue e clique no mapa, ou dois cliques bem rápidos (até 300 ms) em qualquer ponto do talhão."
        )}
        onClick={() => {
          if (tool) chooseTool(null);
          if (typeof onToggleLandplotPickMode === "function") {
            onToggleLandplotPickMode();
          }
        }}
      >
        <FaMapMarkedAlt aria-hidden />
        <span>{t("Talhão")}</span>
      </button>
      <button
        type="button"
        className={`dashboard-quick-measure-btn${
          tool === "area" ? " is-active" : ""
        }`}
        aria-pressed={tool === "area"}
        title={t("Medir área em hectares")}
        disabled={!ready}
        onClick={() => chooseTool("area")}
      >
        <FaDrawPolygon aria-hidden />
        <span>{t("Área")}</span>
      </button>
      <button
        type="button"
        className="dashboard-quick-measure-info"
        aria-expanded={helpOpen}
        aria-label={t("Ajuda sobre medição rápida")}
        title={t("Medição rápida, sem salvar")}
        onClick={() => setHelpOpen((open) => !open)}
      >
        <FaInfoCircle aria-hidden />
      </button>

      {helpOpen ? (
        <div className="dashboard-quick-measure-help" role="dialog">
          <strong>{t("Medição rápida")}</strong>
          <p>
            {t(
              "A régua identifica, com mais precisão, a largura da faixa de trabalho que as máquinas estão usando no campo. Quanto mais faixas entram na média, mais exata fica a leitura."
            )}
          </p>
          <p>
            {t(
              "A área mostra os hectares do polígono, para ver o que já foi feito e o que ainda falta aplicar."
            )}
          </p>
          <p>
            {t(
              "Nada é salvo nesta tela. Para gravar talhões e gerar o mapa da fazenda, use"
            )}{" "}
            <Link to={`/${farmId}/farm-map/`}>{t("Mapa da fazenda")}</Link>.
          </p>
        </div>
      ) : null}

      {tool ? (
        <div className="dashboard-quick-measure-panel">
          {tool === "distance" ? (
            <>
              <p>
                {t(
                  "Esta régua mede a largura da faixa de trabalho no campo, com mais precisão. Clique em um rastro e puxe na transversal, a 90° do deslocamento."
                )}
              </p>
              <p>
                {t(
                  "Quanto mais faixas entrarem na média, mais exata fica a leitura."
                )}
              </p>
              {!hasTracks ? (
                <p>
                  {t(
                    "Ligue os Rastros para a régua cruzar os caminhos GPS da máquina ou do operador."
                  )}
                </p>
              ) : null}
              <div className="dashboard-quick-measure-value">
                <span>
                  {tapeResult && tapeResult.crossingCount >= 2
                    ? t("Largura da faixa")
                    : t("Trena")}
                </span>
                <strong>
                  {tapeResult && tapeResult.crossingCount >= 2
                    ? formatDistanceM(tapeResult.gapAverageM)
                    : tapeResult
                    ? formatDistanceM(tapeResult.tapeLengthM)
                    : "—"}
                </strong>
              </div>
              {tapeResult && tapeResult.crossingCount >= 2 ? (
                <>
                  <div className="dashboard-quick-measure-row">
                    <span>{t("Rastros cruzados")}</span>
                    <strong>{tapeResult.crossingCount}</strong>
                  </div>
                  <div className="dashboard-quick-measure-row">
                    <span>{t("Trena")}</span>
                    <strong>{formatDistanceM(tapeResult.tapeLengthM)}</strong>
                  </div>
                  <p className="dashboard-quick-measure-gaps">
                    {t("Intervalos")}: {formatGaps(tapeResult.gaps)}
                  </p>
                </>
              ) : tapeResult && tapeResult.crossingCount === 1 ? (
                <p>
                  {t(
                    "Puxe por mais faixas. A média fica melhor com várias passadas."
                  )}
                </p>
              ) : tape && tape.origin ? (
                <p>
                  {t(
                    "Nenhum rastro cruzado. Clique no rastro para travar a transversal."
                  )}
                </p>
              ) : null}
              {tape && tape.finished ? (
                <p>{t("Clique de novo para medir em outro lugar.")}</p>
              ) : null}
            </>
          ) : (
            <>
              <p>
                {t(
                  "Clique os cantos da área. Com três pontos os hectares aparecem. Dois cliques concluem."
                )}
              </p>
              <div className="dashboard-quick-measure-value">
                <span>{t("Área")}</span>
                <strong>
                  {points.length < 3 ? "—" : formatAreaHa(areaHa)}
                </strong>
              </div>
            </>
          )}
          <div className="dashboard-quick-measure-actions">
            {tool === "area" && points.length >= 3 && !finished ? (
              <button type="button" onClick={() => setFinished(true)}>
                {t("Concluir")}
              </button>
            ) : null}
            {points.length || tape ? (
              <button type="button" onClick={resetDraft}>
                <FaTimes aria-hidden />
                {t("Limpar")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
